/**
 * Monthly recaps tab (P3.1d).
 *
 * Mounted as the Monthly Recaps tab on /ads-tools. Surfaces the LLM-generated
 * monthly performance recaps that the P3.1 monthly job produces (or that
 * the operator triggers manually). View only — no actions other than
 * "trigger another one for this period".
 */
import { useState } from 'react';
import {
  CalendarDays,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Presentation,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadMonthlyRecapExport,
  getMonthlyRecap,
  listMonthlyRecaps,
  triggerMonthlyRecap,
  type RecapDetail,
  type RecapSummary,
} from '@/services/googleAdsMonthlyService';

interface MonthlyRecapsTabProps {
  tenantId: string;
}

/** Trigger a browser download for a blob (clone of the CampaignQaTab pattern). */
function triggerRecapDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Manages a recap export download with per-format loading state.
 * Returns the current in-flight format (or null) plus a `download` handler.
 */
function useRecapExport(tenantId: string) {
  const [pendingFormat, setPendingFormat] = useState<
    'html' | 'md' | 'pptx' | null
  >(null);

  const download = async (
    reportId: string,
    periodStart: string,
    format: 'html' | 'md' | 'pptx',
  ) => {
    if (pendingFormat) return; // one download at a time
    setPendingFormat(format);
    try {
      const blob = await downloadMonthlyRecapExport(reportId, tenantId, format);
      const filename = `monthly-recap-${tenantId}-${periodStart}.${format}`;
      triggerRecapDownload(blob, filename);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Download failed: ${detail}`);
    } finally {
      setPendingFormat(null);
    }
  };

  return { pendingFormat, download };
}

export function MonthlyRecapsTab({ tenantId }: MonthlyRecapsTabProps) {
  const queryClient = useQueryClient();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  const recaps = useQuery({
    queryKey: ['google-ads-monthly-recaps', tenantId],
    queryFn: () => listMonthlyRecaps(tenantId, 12),
    refetchInterval: 60_000, // monthly cadence — slow refresh is fine
  });

  const triggerMutation = useMutation({
    mutationFn: () => triggerMonthlyRecap({ tenantId }),
    onSuccess: (result) => {
      toast.success(
        `Recap generated for ${result.period_start} → ${result.period_end} ` +
          `(${result.narrative_chars.toLocaleString()} chars)`,
      );
      queryClient.invalidateQueries({
        queryKey: ['google-ads-monthly-recaps', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Trigger failed: ${detail}`);
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-coral" />
            Generate recap
          </CardTitle>
          <CardDescription>
            The monthly job generates one recap per tenant on the 1st of
            each month covering the prior calendar month. Use this button
            for ad-hoc reruns or to backfill a missing period (defaults
            to previous calendar month).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending}
            className="bg-coral hover:bg-coral-hover"
          >
            {triggerMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating… (~30-60s)
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate recap for previous month
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-teal" />
            Recent recaps
          </CardTitle>
          <CardDescription>
            Latest 12 monthly recaps for this tenant, newest first. Click
            a row to read the full narrative.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recaps.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : recaps.data?.length === 0 ? (
            <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No recaps yet. Click "Generate recap" above or wait for the
              monthly job.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">LLM cost</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(recaps.data ?? []).map((r) => (
                  <RecapRow
                    key={r.id}
                    recap={r}
                    tenantId={tenantId}
                    onView={() => setSelectedReportId(r.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RecapDetailDialog
        reportId={selectedReportId}
        tenantId={tenantId}
        onClose={() => setSelectedReportId(null)}
      />
    </div>
  );
}

function RecapRow({
  recap,
  tenantId,
  onView,
}: {
  recap: RecapSummary;
  tenantId: string;
  onView: () => void;
}) {
  const { pendingFormat, download } = useRecapExport(tenantId);
  return (
    <TableRow className="cursor-pointer hover:bg-warm-bg" onClick={onView}>
      <TableCell className="font-medium">
        {formatPeriod(recap.period_start, recap.period_end)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatGenerated(recap.created_at)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        ${recap.llm_cost_usd.toFixed(4)}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Download PowerPoint recap"
            title="Download PowerPoint deck"
            disabled={!!pendingFormat}
            onClick={(e) => {
              e.stopPropagation();
              void download(recap.id, recap.period_start, 'pptx');
            }}
          >
            {pendingFormat === 'pptx' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Presentation className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="icon" aria-label="View recap">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function RecapDetailDialog({
  reportId,
  tenantId,
  onClose,
}: {
  reportId: string | null;
  tenantId: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ['google-ads-monthly-recap', reportId],
    queryFn: () => getMonthlyRecap(reportId!),
    enabled: !!reportId,
  });

  return (
    <Dialog open={!!reportId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Monthly recap</DialogTitle>
          <DialogDescription>
            {detail.data
              ? `${formatPeriod(detail.data.period_start, detail.data.period_end)} · ${detail.data.tenant_id}`
              : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {detail.data && (
          <RecapExportBar recap={detail.data} tenantId={tenantId} />
        )}

        {detail.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading recap…
          </div>
        ) : detail.data ? (
          <div className="space-y-4">
            {detail.data.metrics_json.customer && (
              <div className="rounded border border-border bg-warm-bg p-3 text-xs">
                <div className="mb-2 font-medium">Quick metrics</div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Metric
                    label="Spend"
                    value={`$${(detail.data.metrics_json.customer.spend_usd ?? 0).toFixed(2)}`}
                  />
                  <Metric
                    label="Conversions"
                    value={(
                      detail.data.metrics_json.customer.conversions ?? 0
                    ).toFixed(0)}
                  />
                  <Metric
                    label="CPA"
                    value={`$${(detail.data.metrics_json.customer.cost_per_conversion_usd ?? 0).toFixed(2)}`}
                  />
                  <Metric
                    label="Active campaigns"
                    value={(
                      detail.data.metrics_json.active_campaign_count ?? 0
                    ).toString()}
                  />
                </div>
              </div>
            )}

            {/* CORE-UI-02: dark:prose-invert — the typography plugin's
                default palette paints near-black text whatever the theme;
                the .dark class on <html> flips it to the inverted set. */}
            <div
              data-testid="recap-narrative"
              className="prose prose-sm max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-h2:text-base prose-p:my-2"
            >
              <ReactMarkdown>{detail.data.narrative_md}</ReactMarkdown>
            </div>

            {detail.data.weekly_rollup_json.total_runs !== undefined && (
              <div className="rounded border border-border bg-warm-bg p-3 text-xs">
                <div className="mb-2 font-medium">
                  Weekly rollup over the period
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <Metric
                    label="Runs"
                    value={(
                      detail.data.weekly_rollup_json.total_runs ?? 0
                    ).toString()}
                  />
                  <Metric
                    label="Search terms flagged"
                    value={(
                      detail.data.weekly_rollup_json.search_terms_flagged ??
                      0
                    ).toString()}
                  />
                  <Metric
                    label="Ads paused"
                    value={(
                      detail.data.weekly_rollup_json.ads_paused ?? 0
                    ).toString()}
                  />
                  <Metric
                    label="Keywords paused"
                    value={(
                      detail.data.weekly_rollup_json.keywords_paused ?? 0
                    ).toString()}
                  />
                  <Metric
                    label="Approvals opened"
                    value={(
                      detail.data.weekly_rollup_json
                        .approval_items_created ?? 0
                    ).toString()}
                  />
                  <Metric
                    label="LLM cost (weekly)"
                    value={`$${(
                      detail.data.weekly_rollup_json
                        .total_llm_cost_usd ?? 0
                    ).toFixed(4)}`}
                  />
                </div>
              </div>
            )}

            {/* P5.2 — top competitors from Auction Insights, aggregated
                across weekly windows that overlap this recap's period. */}
            {detail.data.metrics_json.competitors?.top_competitors &&
              detail.data.metrics_json.competitors.top_competitors.length > 0 && (
                <CompetitorsPanel
                  competitors={detail.data.metrics_json.competitors.top_competitors}
                />
              )}

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                Raw aggregates (JSON)
                <ExternalLink className="ml-1 inline h-3 w-3" />
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto rounded bg-warm-bg p-2">
                {JSON.stringify(
                  {
                    metrics_json: detail.data.metrics_json,
                    weekly_rollup_json: detail.data.weekly_rollup_json,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Export controls for the recap dialog. The export carries no operator
 * metrics — the recap is what the client receives.
 */
function RecapExportBar({
  recap,
  tenantId,
}: {
  recap: RecapDetail;
  tenantId: string;
}) {
  const { pendingFormat, download } = useRecapExport(tenantId);
  const disabled = !recap.id || !!pendingFormat;

  return (
    <div className="rounded border border-border bg-warm-bg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => void download(recap.id, recap.period_start, 'html')}
          disabled={disabled}
          className="bg-coral hover:bg-coral-hover"
        >
          {pendingFormat === 'html' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparing report…
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Download report
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => void download(recap.id, recap.period_start, 'md')}
          disabled={disabled}
        >
          {pendingFormat === 'md' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparing…
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Markdown
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => void download(recap.id, recap.period_start, 'pptx')}
          disabled={disabled}
        >
          {pendingFormat === 'pptx' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparing…
            </>
          ) : (
            <>
              <Presentation className="mr-2 h-4 w-4" />
              PowerPoint
            </>
          )}
        </Button>
      </div>
      {/* CORE-G-3: retired the "Client-safe report" framing per Chans's
          spec. The one surviving factual note names what the file does
          NOT contain — no operator metrics — which is the practical
          info an operator needs before hitting Send. */}
      <p className="mt-2 text-xs text-muted-foreground">
        No operator metrics.
      </p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}


/**
 * P5.2 — Top-competitors table inside the recap dialog.
 *
 * Renders the metrics_json.competitors.top_competitors rollup from
 * MonthlyRecapService. Each row = one competitor domain seen in the
 * tenant's auctions across the period, averaged across the weekly
 * P5.1 Auction Insights observation windows.
 */
function CompetitorsPanel({
  competitors,
}: {
  competitors: Array<{
    domain: string;
    avg_impression_share?: number | null;
    avg_overlap_rate?: number | null;
    avg_outranking_share?: number | null;
    observations?: number;
  }>;
}) {
  if (competitors.length === 0) return null;
  return (
    <div className="rounded border border-border bg-warm-bg p-3 text-xs">
      <div className="mb-2 flex items-center justify-between">
        <div className="font-medium">
          Top competitors over the period
        </div>
        <span className="text-[11px] text-muted-foreground">
          From P5 Auction Insights · averaged across weekly windows
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr className="border-b border-border/60">
              <th className="py-1.5 text-left font-medium">Competitor</th>
              <th className="py-1.5 text-right font-medium">Avg impr. share</th>
              <th className="py-1.5 text-right font-medium">Avg overlap</th>
              <th className="py-1.5 text-right font-medium">Avg outranking</th>
              <th className="py-1.5 text-right font-medium">Weeks observed</th>
            </tr>
          </thead>
          <tbody>
            {competitors.map((c, i) => (
              <tr
                key={c.domain}
                className="border-b border-border/30 last:border-0"
              >
                <td className="py-1.5">
                  <span className={i === 0 ? 'font-medium text-coral-dark' : ''}>
                    {c.domain}
                  </span>
                </td>
                <Pct value={c.avg_impression_share} highlight={i === 0} />
                <Pct value={c.avg_overlap_rate} />
                <Pct value={c.avg_outranking_share} />
                <td className="py-1.5 text-right tabular-nums">
                  {c.observations ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Pct({
  value,
  highlight,
}: {
  value: number | null | undefined;
  highlight?: boolean;
}) {
  return (
    <td
      className={
        'py-1.5 text-right tabular-nums ' +
        (highlight ? 'font-medium text-coral-dark' : '')
      }
    >
      {value == null ? '—' : `${(value * 100).toFixed(1)}%`}
    </td>
  );
}

export function formatPeriod(start: string, end: string): string {
  // ISO yyyy-mm-dd → "MMM YYYY" if start = first of month + end = last
  try {
    const s = new Date(`${start}T00:00:00Z`);
    return s.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return `${start} → ${end}`;
  }
}

function formatGenerated(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
